import { Icons } from './Icons';
import type { PreviewData } from '@/types';

interface PreviewPanelProps {
  preview: PreviewData | null;
  onChapterClick?: (chapterIndex: number) => void;
  onChapterEdit?: (chapter: PreviewData['chapters'][number]) => void;
}

export function PreviewPanel({ preview, onChapterClick, onChapterEdit }: PreviewPanelProps) {
  if (!preview) {
    return (
      <div className="text-center py-12 text-stone-400">
        <Icons.Layers />
        <p className="text-sm mt-2">Start writing to see preview</p>
      </div>
    );
  }

  // Format read time
  const formatReadTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Handle undefined values for backwards compatibility
  const totalWordCount = preview.totalWordCount ?? 0;
  const totalReadTime = preview.totalReadTime ?? 0;
  const totalLlmTokens = preview.totalLlmTokens ?? 0;
  const totalTokenCost = preview.totalTokenCost ?? 0;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <p className="text-2xl font-semibold text-stone-800">{preview.chapterCount}</p>
          <p className="text-xs text-stone-400 uppercase tracking-wide mt-1">Chapters</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <p className="text-2xl font-semibold text-stone-800">{preview.audioCount}</p>
          <p className="text-xs text-stone-400 uppercase tracking-wide mt-1">Audio</p>
        </div>
      </div>

      {/* Book Totals */}
      <div className="bg-white rounded-lg border border-stone-200 p-4">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">Book Totals</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xl font-semibold text-stone-800">{totalWordCount.toLocaleString()}</p>
            <p className="text-xs text-stone-400">words</p>
          </div>
          <div>
            <p className="text-xl font-semibold text-stone-800">{formatReadTime(totalReadTime)}</p>
            <p className="text-xs text-stone-400">read time</p>
          </div>
          <div>
            <p className="text-xl font-semibold text-stone-800">{totalLlmTokens.toLocaleString()}</p>
            <p className="text-xs text-stone-400">LLM tokens</p>
          </div>
          <div>
            <p className="text-xl font-semibold text-stone-800">{totalTokenCost}</p>
            <p className="text-xs text-stone-400">unlock cost</p>
          </div>
        </div>
      </div>

      {/* Chapters List */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Chapters</p>
          <p className="text-xs text-stone-400">Click to edit</p>
        </div>
        <div className="divide-y divide-stone-100 max-h-64 overflow-y-auto">
          {preview.chapters.map((ch, index) => (
            <div
              key={ch.sequence}
              className="group px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
            >
              <button
                onClick={() => onChapterClick?.(index)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-400 w-6">{ch.sequence}</span>
                  <p className="text-sm font-medium text-stone-700 truncate">{ch.title}</p>
                </div>
                 <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs text-stone-400">{(ch.wordCount ?? 0).toLocaleString()} words</span>
                  <span className="text-xs text-stone-300">•</span>
                  <span className="text-xs text-stone-400">{formatReadTime(ch.readTime ?? 0)}</span>
                  <span className="text-xs text-stone-300">•</span>
                  <span className="text-xs text-stone-400">{(ch.llmTokens ?? 0).toLocaleString()} tokens</span>
                  <span className="text-xs text-stone-300">•</span>
                  <span className={`text-xs ${ch.tokenCost === 0 ? 'text-teal-600' : 'text-stone-400'}`}>
                    {ch.tokenCost === 0 ? 'Free' : `${ch.tokenCost} unlock`}
                  </span>
                </div>
              </button>
              
              {onChapterEdit && (
                <button
                  onClick={() => onChapterEdit?.(ch)}
                  className="opacity-0 group-hover:opacity-100 p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-all"
                  title="Edit chapter"
                >
                  <Icons.Edit />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Audio Markers */}
      {preview.audios.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Audio Markers</p>
          </div>
          <div className="divide-y divide-stone-100 max-h-48 overflow-y-auto">
            {preview.audios.map((audio) => (
              <div key={audio.audioId} className="px-4 py-3">
                <p className="text-sm text-stone-700">{audio.label}</p>
                <p className="text-xs text-stone-400 mt-0.5">{audio.audioId} • {audio.cost} tokens</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
